export type SubscriptionTier = 'TRIAL' | 'FREE' | 'PROFESSIONAL' | 'ULTIMATE';

export interface Subscription {
  updatedAt: string;
  createdAt: string;
  active: boolean;
  changedByUserId: number | null;
  grantDayOfMonth: number;
  lastCreditGrantAt: string | null;
  creditsGranted: number;
  endedAt: string | null;
  startedAt: string;
  tier: SubscriptionTier;
  userId: number;
  id: number;
  /** Paid through the payment provider; such a subscription is changed in the billing portal. */
  providerManaged: boolean;
  /** Payment state of a provider-managed subscription, null otherwise. */
  paymentStatus: PaymentStatus | null;
  /** End of the paid period: when it renews, or ends if `cancelAtPeriodEnd`. */
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

type PaymentStatus = 'ACTIVE' | 'PAST_DUE' | 'INCOMPLETE' | 'CANCELED';

/** Tiers sold through the payment provider. */
export type PaidTier = Extract<SubscriptionTier, 'PROFESSIONAL' | 'ULTIMATE'>;

/** A price as the backend reports it: `amount` in the currency's minor unit (cents). */
interface BillingPrice {
  amount: number;
  /** Lower-case ISO 4217 code. */
  currency: string;
}

interface BillingPlan extends BillingPrice {
  tier: PaidTier;
  monthlyCredits: number;
}

interface BillingCreditPack extends BillingPrice {
  key: string;
  credits: number;
}

/** `GET /billing/catalog`: what can be bought, with prices. */
export interface BillingCatalog {
  plans: BillingPlan[];
  creditPacks: BillingCreditPack[];
}

/** Answer of the checkout and portal endpoints: the payment provider's page to go to. */
export interface BillingRedirect {
  url: string;
}

export interface AssignSubscriptionRequest {
  tier: SubscriptionTier;
}

export interface PageResponse<T> {
  content: T[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export type SortDirection = 'ASC' | 'DESC';

/** Row of the admin `GET /subscriptions` list: the active subscription with the user's name and email. */
export interface SubscriptionListItem {
  id: number;
  userId: number;
  userEmail: string;
  userFullName: string | null;
  /** When the user registered (account created). */
  userRegisteredAt: string;
  tier: SubscriptionTier;
  startedAt: string;
  creditsGranted: number;
  lastCreditGrantAt: string | null;
  grantDayOfMonth: number;
  createdAt: string;
  /** Paid through the payment provider: the user is charged for it. */
  providerManaged: boolean;
  /** That provider, e.g. "stripe"; null when not paid through a provider. */
  paymentProvider: string | null;
}

export type SubscriptionListSort = 'STARTED_AT' | 'TIER' | 'CREDITS' | 'EMAIL' | 'NAME' | 'REGISTERED';

export interface SubscriptionListQuery {
  tier?: SubscriptionTier;
  search?: string;
  /** ISO instants, both inclusive. */
  registeredFrom?: string;
  registeredTo?: string;
  sort?: SubscriptionListSort;
  direction?: SortDirection;
  page?: number;
  pageSize?: number;
}

export const buildSubscriptionListUrl = (query: SubscriptionListQuery): string => {
  const params = new URLSearchParams();
  params.set('page', String(query.page ?? 0));
  params.set('pageSize', String(query.pageSize ?? 25));
  if (query.tier) params.set('tier', query.tier);
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.registeredFrom) params.set('registeredFrom', query.registeredFrom);
  if (query.registeredTo) params.set('registeredTo', query.registeredTo);
  if (query.sort) params.set('sort', query.sort);
  if (query.direction) params.set('direction', query.direction);
  return `/subscriptions?${params.toString()}`;
};
